import Ajv from 'ajv';
import { useEffect, useRef, useState } from 'react';
import type { DeskSnapshot, PatternAlert } from './generated/types.gen';
import { familyOf, type Place } from '../market';

/** Snapshots recorded before range buckets carry no family; theirs follows from the horizon. */
export const snapshotFamily = (s: DeskSnapshot) => s.family ?? (s.horizon === '15m' ? 'up_down' : 'above_below');

export async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(path, { cache: 'no-store', signal });
  if (!r.ok) throw new Error(r.status === 503 ? 'Waiting for recorded market data' : `Request failed (${r.status})`);
  return r.json() as Promise<T>;
}

export function useDesk(place: Place, ticker: string | null, replay: string | null) {
  const [data, setData] = useState<DeskSnapshot | null>(null);
  // Plan 073: what is setting up on every coin; null until the stream has said.
  const [alerts, setAlerts] = useState<PatternAlert[] | null>(null);
  const [connection, setConnection] = useState('connecting');
  const [error, setError] = useState('');
  const [resume, setResume] = useState(0);
  const received = useRef(0);
  useEffect(() => {
    // Coming back to the app (or back online) reconnects on purpose, like a switch: no warning unless it is slow (plan 029).
    const wake = () => { setConnection('connecting'); setResume(v => v + 1); };
    const visibility = () => { if (document.visibilityState === 'visible') wake(); else { setConnection('backgrounded'); setResume(v => v + 1); } };
    window.addEventListener('online', wake);
    const offline = () => { setConnection('offline'); setResume(v => v + 1); };
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visibility);
    return () => { window.removeEventListener('online', wake); window.removeEventListener('offline', offline); document.removeEventListener('visibilitychange', visibility); };
  }, []);
  useEffect(() => {
    if (document.visibilityState !== 'visible' || !navigator.onLine) {
      setConnection(navigator.onLine ? 'backgrounded' : 'offline');
      return;
    }
    let dead = false;
    let ws: WebSocket | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    setConnection('connecting'); setError('');
    const params = new URLSearchParams();
    if (ticker) params.set('market_ticker', ticker);
    const query = new URLSearchParams(params);
    if (place.family) query.set('family', place.family);
    params.set('coin', place.coin); params.set('horizon', place.horizon);
    if (place.family) params.set('family', place.family);
    const family = familyOf(place);
    const accept = (s: DeskSnapshot) => {
      if (dead) return;
      received.current = Date.now();
      // Never let an older revision of the same market replace a newer one, nor another market's answer arrive late.
      if ((s.coin ?? 'BTC') !== place.coin || (s.horizon ?? 'hourly') !== place.horizon || snapshotFamily(s) !== family) return;
      setData(previous => previous && previous.mode === s.mode && previous.coin === s.coin && previous.horizon === s.horizon && previous.family === s.family && previous.selected?.ticker === s.selected?.ticker && previous.revision > s.revision ? previous : s);
      setConnection(replay ? 'replay' : 'connected'); setError('');
    };
    const connect = async () => {
      try {
        if (replay) query.set('view_id', replay);
        const s = await get<DeskSnapshot>(`/api/v1/desk/${place.coin}/${place.horizon}?${query}`, controller.signal);
        accept(s);
        if (replay || dead) return;
        const schema = await get<object>('/api/v1/stream-schema', controller.signal);
        const validate = new Ajv({ strict: false, validateFormats: false }).compile(schema);
        ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/v1/stream?${params}`);
        ws.onmessage = event => {
          try {
            const m: unknown = JSON.parse(event.data);
            if (!validate(m)) throw new Error('Incompatible data version; reload the workspace');
            const message = m as { type: string; snapshot: DeskSnapshot | null; alerts?: PatternAlert[] };
            if (message.alerts) setAlerts(message.alerts);
            if (message.type === 'snapshot' && message.snapshot) accept(message.snapshot);
            else if (message.type === 'unavailable') setConnection('waiting');
          } catch (e) { setError(String(e)); setConnection('incompatible'); ws?.close(); }
        };
        ws.onclose = () => { if (!dead) { setConnection('reconnecting'); retry = setTimeout(connect, 2000); } };
        ws.onerror = () => ws?.close();
      } catch (e) {
        if (!dead) { setError(e instanceof Error ? e.message : 'Connection unavailable'); setConnection('offline'); retry = setTimeout(connect, 3000); }
      }
    };
    void connect();
    const watchdog = setInterval(() => {
      if (!replay && received.current && Date.now() - received.current > 10000) { setConnection('stale'); ws?.close(); }
    }, 2000);
    return () => { dead = true; controller.abort(); ws?.close(); clearTimeout(retry); clearInterval(watchdog); };
  }, [place.coin, place.horizon, place.family, ticker, replay, resume]);
  return { data, connection, error, alerts };
}
